/**
 * Code Retrieval Modal Component
 * Modal for viewing and downloading redeem codes with filtering
 * Supports pagination and CSV export with pre-ticked "unused only" filter
 * Now includes tabbed navigation for "All Codes" and "Generation History"
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useGroupCodes } from '../../hooks/useRedeemGroupApi';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { formatDate } from '../../lib/utils/formatters';
import { GenerationHistorySection } from './GenerationHistorySection';
import toast from 'react-hot-toast';

interface CodeRetrievalModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
  groupName: string;
}

type TabType = 'all-codes' | 'generation-history';

export function CodeRetrievalModal({
  isOpen,
  onClose,
  groupId,
  groupName,
}: CodeRetrievalModalProps) {
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('all-codes');

  // Pre-ticked checkbox: defaults to checked (only show unused codes)
  const [showUnusedOnly, setShowUnusedOnly] = useState(true);

  // CSV download state
  const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);

  // Fetch codes with filter (redeemed=false means unused codes)
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useGroupCodes(groupId, showUnusedOnly ? false : undefined);

  // Flatten all pages into single array
  const allCodes = useMemo(() => {
    return data?.pages.flatMap(page => page.codes) || [];
  }, [data?.pages]);

  // Virtual scrolling setup - renders only visible rows (~20-40) instead of entire dataset
  // This dramatically reduces DOM nodes from 2K+ to ~50, improving performance
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: allCodes.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 52, // Row height in pixels - must match h-[52px] class on rows
    overscan: 5, // Render 5 extra rows above/below viewport for smooth scrolling
  });

  // Scroll-triggered infinite pagination
  // Automatically loads next page when user scrolls near bottom of the codes table.
  // Uses requestAnimationFrame to prevent layout thrashing during rapid scroll events.
  // Load More button is preserved as accessible fallback for keyboard-only users.
  useEffect(() => {
    const scrollElement = parentRef.current;
    if (!scrollElement) return;

    let rafId: number | null = null;

    const handleScroll = () => {
      // Throttle via requestAnimationFrame to prevent layout thrashing
      if (rafId !== null) return;

      rafId = requestAnimationFrame(() => {
        rafId = null;

        // Guard: Don't trigger if already fetching or no more pages available
        if (isFetchingNextPage || !hasNextPage) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollElement;
        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

        // Trigger pagination at 80% scroll depth (typically ~200px before bottom)
        // This threshold provides smooth UX - new data loads before user hits bottom
        if (scrollPercentage > 0.8) {
          fetchNextPage();
        }
      });
    };

    scrollElement.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
    // Include activeTab to re-attach scroll listener when switching back to All Codes tab
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, activeTab]);

  const handleFilterToggle = () => {
    setShowUnusedOnly(!showUnusedOnly);
  };

  const handleDownloadCSV = async (unredeemedOnly: boolean) => {
    setShowDownloadMenu(false);
    setIsDownloadingCSV(true);
    try {
      // Use backend streaming CSV endpoint
      const { authenticatedApi } = await import('../../lib/authenticated-api');
      const blob = await authenticatedApi.downloadGroupCodesCSV(groupId, {
        redeemed: unredeemedOnly ? false : undefined,
      });

      // Generate filename
      const timestamp = new Date()
        .toISOString()
        .replace(/[:.]/g, '-')
        .slice(0, -5);
      const sanitizedGroupName = groupName.replace(/[^a-zA-Z0-9-_]/g, '_');
      const suffix = unredeemedOnly ? '-unredeemed' : '';
      const filename = `redeem-codes-${sanitizedGroupName}${suffix}-${timestamp}.csv`;

      // Trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Delay revocation to ensure download completes
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 5000);
    } catch (err) {
      console.error('Failed to download CSV:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to download CSV';
      toast.error(errorMessage);
    } finally {
      setIsDownloadingCSV(false);
    }
  };

  const handleClose = () => {
    // Reset filter and tab state when closing
    setShowUnusedOnly(true);
    setActiveTab('all-codes');
    onClose();
  };

  // Handle Escape key to close modal (must be before early return)
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setShowUnusedOnly(true);
        setActiveTab('all-codes');
        setShowDownloadMenu(false);
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isOpen, onClose]);

  // Close download menu when clicking outside
  useEffect(() => {
    if (!showDownloadMenu) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest('[aria-haspopup="true"]') &&
        !target.closest('[role="menu"]')
      ) {
        setShowDownloadMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDownloadMenu]);

  if (!isOpen) return null;

  // Handle backdrop keyboard interaction
  const handleBackdropKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          role="button"
          tabIndex={0}
          onClick={handleClose}
          onKeyDown={handleBackdropKeyDown}
          aria-label="Close modal by clicking backdrop"
        />

        {/* Modal */}
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="code-retrieval-modal-title"
          className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3
                id="code-retrieval-modal-title"
                className="text-lg font-medium text-gray-900 dark:text-gray-100"
              >
                View Codes
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {groupName}
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              aria-label="Close modal"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('all-codes')}
                className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'all-codes'
                    ? 'border-sky-500 text-sky-600 dark:text-sky-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
                aria-current={activeTab === 'all-codes' ? 'page' : undefined}
              >
                All Codes
              </button>
              <button
                onClick={() => setActiveTab('generation-history')}
                className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'generation-history'
                    ? 'border-sky-500 text-sky-600 dark:text-sky-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
                aria-current={
                  activeTab === 'generation-history' ? 'page' : undefined
                }
              >
                Generation History
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          {activeTab === 'all-codes' ? (
            <>
              {/* Filter Controls */}
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="unused-only-filter"
                    checked={showUnusedOnly}
                    onChange={handleFilterToggle}
                    className="w-4 h-4 text-sky-600 border-gray-300 dark:border-gray-600 rounded focus:ring-sky-500 dark:bg-gray-700"
                  />
                  <label
                    htmlFor="unused-only-filter"
                    className="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                  >
                    Only show unused codes
                  </label>
                </div>

                {/* Download CSV Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                    disabled={isDownloadingCSV}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-haspopup="true"
                    aria-expanded={showDownloadMenu}
                  >
                    {isDownloadingCSV ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                          />
                        </svg>
                        Download CSV
                        <svg
                          className="w-4 h-4 ml-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </>
                    )}
                  </button>
                  {showDownloadMenu && (
                    <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-700 ring-1 ring-black ring-opacity-5 z-50">
                      <div
                        className="py-1"
                        role="menu"
                        aria-orientation="vertical"
                      >
                        <button
                          onClick={() => handleDownloadCSV(false)}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                          role="menuitem"
                        >
                          Download all codes
                        </button>
                        <button
                          onClick={() => handleDownloadCSV(true)}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                          role="menuitem"
                        >
                          Download unredeemed codes only
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Stats */}
              {data?.pages[0] && (
                <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing{' '}
                    <span className="font-medium">{allCodes.length}</span> of{' '}
                    <span className="font-medium">
                      {data.pages[0].totalCount.toLocaleString()}
                    </span>{' '}
                    {showUnusedOnly ? 'unused ' : ''}codes
                  </p>
                </div>
              )}

              {/* Codes Table */}
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                {isLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <LoadingSpinner size="lg" />
                  </div>
                ) : error ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                      <p className="text-gray-500 dark:text-gray-400 mb-2">
                        Failed to load codes
                      </p>
                      <button
                        onClick={() => refetch()}
                        className="text-sky-600 hover:text-sky-500 dark:text-sky-400 dark:hover:text-sky-300 font-medium"
                      >
                        Try again
                      </button>
                    </div>
                  </div>
                ) : allCodes.length === 0 ? (
                  <div className="flex items-center justify-center h-64">
                    <p className="text-gray-500 dark:text-gray-400">
                      {showUnusedOnly
                        ? 'No unused codes found'
                        : 'No codes found for this group'}
                    </p>
                  </div>
                ) : (
                  <div
                    role="table"
                    aria-label="Redeem codes"
                    aria-rowcount={allCodes.length + 1}
                    aria-colcount={5}
                    className="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                  >
                    {/* Header row - sticky */}
                    <div
                      role="rowgroup"
                      className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10"
                    >
                      <div
                        role="row"
                        aria-rowindex={1}
                        className="grid grid-cols-[minmax(180px,1fr)_100px_minmax(120px,1fr)_140px_140px]"
                      >
                        <div
                          role="columnheader"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                          Code
                        </div>
                        <div
                          role="columnheader"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                          Status
                        </div>
                        <div
                          role="columnheader"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                          Redeemed By
                        </div>
                        <div
                          role="columnheader"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                          Redeemed At
                        </div>
                        <div
                          role="columnheader"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                          Generated At
                        </div>
                      </div>
                    </div>
                    {/* Virtualized body */}
                    <div
                      ref={parentRef}
                      role="rowgroup"
                      className="overflow-auto max-h-96 bg-white dark:bg-gray-800"
                    >
                      <div
                        style={{
                          height: `${virtualizer.getTotalSize()}px`,
                          position: 'relative',
                        }}
                      >
                        {virtualizer.getVirtualItems().map(virtualRow => {
                          const code = allCodes[virtualRow.index];
                          return (
                            <div
                              key={code.code}
                              role="row"
                              aria-rowindex={virtualRow.index + 2}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: `${virtualRow.size}px`,
                                transform: `translateY(${virtualRow.start}px)`,
                              }}
                              className="grid grid-cols-[minmax(180px,1fr)_100px_minmax(120px,1fr)_140px_140px] h-[52px] hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700"
                            >
                              <div
                                role="cell"
                                className="px-6 py-4 min-w-0 text-sm font-mono text-gray-900 dark:text-gray-100 flex items-center"
                              >
                                <span className="truncate">{code.code}</span>
                              </div>
                              <div
                                role="cell"
                                className="px-6 py-4 flex items-center"
                              >
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    code.redeemed
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                  }`}
                                >
                                  {code.redeemed ? 'Redeemed' : 'Unused'}
                                </span>
                              </div>
                              <div
                                role="cell"
                                className="px-6 py-4 min-w-0 text-sm text-gray-600 dark:text-gray-400 flex items-center"
                              >
                                <span className="truncate">
                                  {code.redeemedBy || '-'}
                                </span>
                              </div>
                              <div
                                role="cell"
                                className="px-6 py-4 min-w-0 text-sm text-gray-600 dark:text-gray-400 flex items-center"
                              >
                                <span className="truncate">
                                  {formatDate(code.redeemedAt)}
                                </span>
                              </div>
                              <div
                                role="cell"
                                className="px-6 py-4 min-w-0 text-sm text-gray-600 dark:text-gray-400 flex items-center"
                              >
                                <span className="truncate">
                                  {formatDate(code.generated_at)}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Load More Button */}
              {hasNextPage && !isLoading && !error && (
                <div className="mt-4">
                  <button
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isFetchingNextPage ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </>
          ) : (
            /* Generation History Tab */
            <GenerationHistorySection groupId={groupId} groupName={groupName} />
          )}

          {/* Close Button */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
